import { useState } from 'react'
import { Check, Info } from 'lucide-react'

import type { AnswerMap, AnswerValue, PhaseInfo, WorkflowStage } from '../../types/workflow'

interface PastGlp1TemplateProps {
  stage: WorkflowStage
  onSelect: (value: AnswerValue) => void
  currentPhase?: PhaseInfo | null
  answers?: AnswerMap
}

const PAST_MEDICATIONS = [
  { id: 'ozempic', label: 'Ozempic® (Semaglutide)', isTirzepatide: false },
  { id: 'wegovy', label: 'Wegovy® (Semaglutide)', isTirzepatide: false },
  { id: 'mounjaro', label: 'Mounjaro® (Tirzepatide)', isTirzepatide: true },
  { id: 'zepbound', label: 'Zepbound® (Tirzepatide)', isTirzepatide: true },
  { id: 'compounded_semaglutide', label: 'Compounded Semaglutide [CarniSema®]', isTirzepatide: false },
  { id: 'compounded_tirzepatide', label: 'Compounded Tirzepatide [CarniTide®]', isTirzepatide: true },
  { id: 'other', label: 'Other / Not sure', isTirzepatide: false },
]

const DOSAGE_OPTIONS = {
  semaglutide: ['0.25 mg', '0.5 mg', '1 mg', '1.7 mg', '2.4 mg', 'Not sure'],
  tirzepatide: ['2.5 mg', '5 mg', '7.5 mg', '10 mg', '12.5 mg', '15 mg', 'Not sure'],
  compounded: ['Low dose', 'Medium dose', 'High dose', 'Not sure'],
} as const

function getDosageType(medicationId: string): keyof typeof DOSAGE_OPTIONS {
  if (['ozempic', 'wegovy', 'compounded_semaglutide'].includes(medicationId)) return 'semaglutide'
  if (['mounjaro', 'zepbound', 'compounded_tirzepatide'].includes(medicationId)) return 'tirzepatide'
  return 'compounded'
}

const LAST_DOSE_TIMING = [
  { id: 'within_week', label: 'Within the past week' },
  { id: '1_4_weeks', label: '1-4 weeks ago' },
  { id: '1_3_months', label: '1-3 months ago' },
  { id: '3_6_months', label: '3-6 months ago' },
  { id: 'more_6_months', label: 'More than 6 months ago' },
  { id: 'not_sure', label: "Hmm, I'm not sure" },
]

const DURATION_ON_MEDICATION = [
  { id: 'less_than_month', label: 'Less than a month' },
  { id: '1_to_3_months', label: '1-3 months' },
  { id: '3_to_6_months', label: '3-6 months' },
  { id: '6_to_12_months', label: '6-12 months' },
  { id: 'over_year', label: 'Over a year' },
  { id: 'not_sure', label: "I'm not sure" },
]

const STOP_REASONS = [
  { id: 'cost', label: 'Cost / insurance issues' },
  { id: 'side_effects', label: 'Side effects were too difficult' },
  { id: 'reached_goal', label: 'Reached my goal weight' },
  { id: 'not_working', label: "It wasn't working for me" },
  { id: 'supply', label: 'Supply/availability issues' },
  { id: 'provider', label: 'My provider stopped prescribing it' },
  { id: 'other', label: 'Other reason' },
]

const TOLERANCE_OPTIONS = [
  { id: 'very_well', label: 'Very well - minimal side effects' },
  { id: 'manageable', label: 'Some side effects but manageable' },
  { id: 'significant', label: 'Significant side effects' },
  { id: 'could_not_tolerate', label: 'Could not tolerate it' },
]

const EFFICACY_OPTIONS = [
  { id: 'very_effective', label: 'Very effective - lost significant weight' },
  { id: 'somewhat_effective', label: 'Somewhat effective - lost some weight' },
  { id: 'not_effective', label: 'Not very effective' },
  { id: 'too_short', label: "I didn't use it long enough to tell" },
]

type DosageData = Record<string, string>

export default function PastGlp1Template({ stage, onSelect, currentPhase, answers }: PastGlp1TemplateProps) {
  const glp1Experience = answers?.glp1_experience as string | undefined
  const currentMedication =
    (answers?.current_medication as string | undefined) ||
    (Array.isArray(answers?.current_glp1_medications)
      ? (answers?.current_glp1_medications as string[])[0]
      : undefined)
  const preselectedMedications = Array.isArray(answers?.current_glp1_medications)
    ? (answers.current_glp1_medications as string[]).filter(Boolean)
    : currentMedication
      ? [currentMedication]
      : []
  const isCurrentlyTaking = glp1Experience === 'currently_taking' || stage.id === 'current_glp1_details'

  const [step, setStep] = useState<'medications' | 'dosage' | 'timing' | 'experience'>(
    preselectedMedications.length > 0 ? 'dosage' : 'medications',
  )
  const [selectedMedications, setSelectedMedications] = useState<string[]>(
    preselectedMedications,
  )
  const [dosageData, setDosageData] = useState<DosageData>({})
  const [lastDoseTiming, setLastDoseTiming] = useState('')
  const [stopReason, setStopReason] = useState('')
  const [tolerance, setTolerance] = useState('')
  const [efficacy, setEfficacy] = useState('')

  const handleMedicationToggle = (medId: string) => {
    setSelectedMedications((prev) => (prev.includes(medId) ? prev.filter((id) => id !== medId) : [...prev, medId]))
  }

  const handleDosageSelect = (medId: string, dosage: string) => {
    setDosageData((prev) => ({ ...prev, [medId]: dosage }))
  }

  const handleNextFromMedications = () => {
    if (selectedMedications.length === 0) return
    if (selectedMedications.length === 1 && selectedMedications[0] === 'other') {
      setStep('timing')
    } else {
      setStep('dosage')
    }
  }

  const handleNextFromDosage = () => {
    const allDosagesCollected = selectedMedications.every(
      (medId) => dosageData[medId] || medId === 'other',
    )
    if (!allDosagesCollected) return
    setStep('timing')
  }

  const handleNextFromTiming = () => {
    if (!lastDoseTiming) return
    if (isCurrentlyTaking) {
      handleContinue()
    } else {
      setStep('experience')
    }
  }

  const canContinueFromMedications = selectedMedications.length > 0
  const canContinueFromDosage = selectedMedications.every(
    (medId) => dosageData[medId] || medId === 'other',
  )
  const canContinueFromTiming = lastDoseTiming !== ''
  const canContinueFromExperience = !!stopReason && !!tolerance && !!efficacy

  const handleContinue = () => {
    const hasTirzepatide = selectedMedications.some((medId) => PAST_MEDICATIONS.find((m) => m.id === medId)?.isTirzepatide)
    const toleratedWell = tolerance === 'very_well' || tolerance === 'manageable'
    const suggestCarniTide = hasTirzepatide && toleratedWell
    const needsClinicalReview =
      !isCurrentlyTaking && (tolerance === 'could_not_tolerate' || (stopReason === 'side_effects' && tolerance === 'significant'))

    if (isCurrentlyTaking) {
      onSelect({
        current_glp1_medications: selectedMedications,
        current_glp1_dosages: dosageData,
        glp1_duration: lastDoseTiming,
        had_tirzepatide: hasTirzepatide,
      })
    } else {
      onSelect({
        past_glp1_medications: selectedMedications,
        past_glp1_dosages: dosageData,
        glp1_last_dose_timing: lastDoseTiming,
        glp1_discontinuation_reason: stopReason,
        glp1_tolerance: tolerance,
        glp1_efficacy: efficacy,
        had_tirzepatide: hasTirzepatide,
        suggest_carnitide: suggestCarniTide,
        needs_clinical_review: needsClinicalReview,
      })
    }
  }

  const handleBack = () => {
    if (step === 'dosage') setStep('medications')
    else if (step === 'timing') setStep('dosage')
    else if (step === 'experience') setStep('timing')
  }

  const stepInfo: Record<string, { title: string; progress: number; total: number }> = isCurrentlyTaking
    ? {
        dosage: { title: 'Do you remember what dose you were on?', progress: 1, total: 2 },
        timing: { title: 'How long have you been on it?', progress: 2, total: 2 },
      }
    : {
        medications: { title: 'Got it — which ones have you tried?', progress: 1, total: 4 },
        dosage: { title: 'Do you remember what dose you were on?', progress: 2, total: 4 },
        timing: { title: 'When was your last dose?', progress: 3, total: 4 },
        experience: { title: 'Your experience', progress: 4, total: 4 },
      }

  const currentStepInfo = stepInfo[step] || stepInfo[Object.keys(stepInfo)[0]]

  const canContinue =
    (step === 'medications' && canContinueFromMedications) ||
    (step === 'dosage' && canContinueFromDosage) ||
    (step === 'timing' && canContinueFromTiming) ||
    (step === 'experience' && canContinueFromExperience)

  return (
    <div className="flex-1 flex flex-col gap-4 overflow-y-auto">

      {step === 'medications' && (
        <div className="mb-6">
          <p className="mb-3 text-[13px] font-semibold uppercase tracking-wide text-[var(--ww-text-3)]">Select all that apply</p>
          <div className="space-y-2">
            {PAST_MEDICATIONS.map((med) => {
              const isSelected = selectedMedications.includes(med.id)
              return (
                <button
                  key={med.id}
                  onClick={() => handleMedicationToggle(med.id)}
                  className={`flex w-full items-center gap-3.5 rounded-2xl border px-[18px] py-4 text-left transition-all duration-300 ${
                    isSelected ? 'border-[var(--ww-accent)] choice-selected text-[var(--ww-text)] selected-glow' : 'border-[var(--ww-glass-border)] bg-[var(--ww-surface)] text-[var(--ww-text-2)] hover:border-[var(--ww-border-hover)] hover:bg-[var(--ww-surface-2)]'
                  }`}
                >
                  <div className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border-2 transition-all duration-200 ${isSelected ? 'bg-[var(--ww-accent)] border-[var(--ww-accent)]' : 'border-[var(--ww-text-3)]'}`}>
                    {isSelected && <Check className="h-3 w-3 text-[#0a0a0a]" strokeWidth={3} />}
                  </div>
                  <span className="text-[15px] font-medium">{med.label}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {step === 'dosage' && (
        <div className="mb-6">
          {selectedMedications
            .filter((id) => id !== 'other')
            .map((medId) => {
              const med = PAST_MEDICATIONS.find((m) => m.id === medId)
              const dosageType = getDosageType(medId)
              const dosages = DOSAGE_OPTIONS[dosageType] || DOSAGE_OPTIONS.compounded
              return (
                <fieldset key={medId} className="mb-6 flex flex-col gap-2 border-none">
                  <legend className="mb-1.5 text-[13px] font-medium text-[var(--ww-text-2)]">
                    {med ? med.label : medId}
                  </legend>
                  <div className="flex flex-wrap gap-2">
                    {dosages.map((dosage) => (
                      <button
                        key={dosage}
                        type="button"
                        onClick={() => handleDosageSelect(medId, dosage)}
                        className={`rounded-2xl border px-5 py-3 text-[14px] font-medium transition-all duration-300 ${
                          dosageData[medId] === dosage
                            ? 'border-[var(--ww-accent)] choice-selected text-[var(--ww-text)] selected-glow'
                            : 'border-[var(--ww-glass-border)] bg-[var(--ww-surface)] text-[var(--ww-text-2)] hover:border-[var(--ww-border-hover)] hover:bg-[var(--ww-surface-2)]'
                        }`}
                      >
                        {dosage}
                      </button>
                    ))}
                  </div>
                </fieldset>
              )
            })}
        </div>
      )}

      {step === 'timing' && (
        <div className="mb-6">
          {!isCurrentlyTaking && (
            <div className="mb-4 flex items-start gap-3 rounded-xl bg-[var(--ww-surface)] border border-[var(--ww-glass-border)] p-4">
              <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-[var(--ww-accent)]" />
              <p className="text-xs leading-relaxed text-[var(--ww-text-2)]">
                This helps your clinician determine if you can resume at your previous dose or need to restart at a lower dose for safety.
              </p>
            </div>
          )}
          <div className="space-y-2">
            {(isCurrentlyTaking ? DURATION_ON_MEDICATION : LAST_DOSE_TIMING).map((timing) => (
              <button
                key={timing.id}
                onClick={() => setLastDoseTiming(timing.id)}
                className={`flex w-full items-center gap-3.5 rounded-2xl border px-[18px] py-4 text-left transition-all duration-300 ${
                  lastDoseTiming === timing.id ? 'border-[var(--ww-accent)] choice-selected text-[var(--ww-text)] selected-glow' : 'border-[var(--ww-glass-border)] bg-[var(--ww-surface)] text-[var(--ww-text-2)] hover:border-[var(--ww-border-hover)] hover:bg-[var(--ww-surface-2)]'
                }`}
              >
                <div className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200 ${lastDoseTiming === timing.id ? 'border-[var(--ww-accent)]' : 'border-[var(--ww-text-3)]'}`}>
                  {lastDoseTiming === timing.id && <div className="h-2 w-2 rounded-full bg-[var(--ww-accent)]" />}
                </div>
                <span className="text-[15px] font-medium">{timing.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 'experience' && (
        <div className="mb-6">
          <div className="mb-6">
            <p className="mb-3 text-[13px] font-semibold uppercase tracking-wide text-[var(--ww-text-3)]">Why did you stop?</p>
            <div className="space-y-2">
              {STOP_REASONS.map((reason) => (
                <button
                  key={reason.id}
                  onClick={() => setStopReason(reason.id)}
                  className={`flex w-full items-center gap-3.5 rounded-2xl border px-[18px] py-4 text-left transition-all duration-300 ${stopReason === reason.id ? 'border-[var(--ww-accent)] choice-selected text-[var(--ww-text)] selected-glow' : 'border-[var(--ww-glass-border)] bg-[var(--ww-surface)] text-[var(--ww-text-2)] hover:border-[var(--ww-border-hover)] hover:bg-[var(--ww-surface-2)]'}`}
                >
                  <div className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200 ${stopReason === reason.id ? 'border-[var(--ww-accent)]' : 'border-[var(--ww-text-3)]'}`}>
                    {stopReason === reason.id && <div className="h-2 w-2 rounded-full bg-[var(--ww-accent)]" />}
                  </div>
                  <span className="text-[15px] font-medium">{reason.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="mb-6">
            <p className="mb-3 text-[13px] font-semibold uppercase tracking-wide text-[var(--ww-text-3)]">How did you tolerate it?</p>
            <div className="space-y-2">
              {TOLERANCE_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setTolerance(option.id)}
                  className={`flex w-full items-center gap-3.5 rounded-2xl border px-[18px] py-4 text-left transition-all duration-300 ${tolerance === option.id ? 'border-[var(--ww-accent)] choice-selected text-[var(--ww-text)] selected-glow' : 'border-[var(--ww-glass-border)] bg-[var(--ww-surface)] text-[var(--ww-text-2)] hover:border-[var(--ww-border-hover)] hover:bg-[var(--ww-surface-2)]'}`}
                >
                  <div className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200 ${tolerance === option.id ? 'border-[var(--ww-accent)]' : 'border-[var(--ww-text-3)]'}`}>
                    {tolerance === option.id && <div className="h-2 w-2 rounded-full bg-[var(--ww-accent)]" />}
                  </div>
                  <span className="text-[15px] font-medium">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="mb-6">
            <p className="mb-3 text-[13px] font-semibold uppercase tracking-wide text-[var(--ww-text-3)]">How effective was it for you?</p>
            <div className="space-y-2">
              {EFFICACY_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setEfficacy(option.id)}
                  className={`flex w-full items-center gap-3.5 rounded-2xl border px-[18px] py-4 text-left transition-all duration-300 ${efficacy === option.id ? 'border-[var(--ww-accent)] choice-selected text-[var(--ww-text)] selected-glow' : 'border-[var(--ww-glass-border)] bg-[var(--ww-surface)] text-[var(--ww-text-2)] hover:border-[var(--ww-border-hover)] hover:bg-[var(--ww-surface-2)]'}`}
                >
                  <div className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200 ${efficacy === option.id ? 'border-[var(--ww-accent)]' : 'border-[var(--ww-text-3)]'}`}>
                    {efficacy === option.id && <div className="h-2 w-2 rounded-full bg-[var(--ww-accent)]" />}
                  </div>
                  <span className="text-[15px] font-medium">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="mt-auto pt-6 pb-2 -ml-[42px] w-[calc(100%+42px)] space-y-3">
        <button
          onClick={() => {
            if (step === 'medications') handleNextFromMedications()
            else if (step === 'dosage') handleNextFromDosage()
            else if (step === 'timing') handleNextFromTiming()
            else if (step === 'experience') handleContinue()
          }}
          disabled={!canContinue}
          className="w-full py-4 px-6 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 disabled:opacity-25 disabled:cursor-default"
        >
          {step === 'dosage' || step === 'experience' ? 'Continue' : 'Next'}
        </button>
        {step !== 'medications' && (
          <button onClick={handleBack} className="w-full py-3 text-sm font-medium text-[var(--ww-text-3)] transition-colors hover:text-[var(--ww-text)]">
            Go Back
          </button>
        )}
      </div>
    </div>
  )
}
